import { FormControlLabel, RadioGroup, Stack, Typography } from '@mui/material';
import React from 'react';

import {
    addAccountButtonSx,
    mobilePaymentMethodSegmentSx,
    mobilePaymentMethodTrackSx,
} from '../../pages/dashboard/DashboardStyles';
import { useTranslation } from '../../utils/translations';
import ButtonComponent from '../button/ButtonComponent';
import { CustomRadio } from '../radio';
import { colors } from '../theme/theme';

import type { PaymentMethodType } from '../../hooks/make-payment/usePaymentRows';

interface PaymentMethodSelectorProps {
    /** Mobile header uses a full-width wrapper + fit-content pill; desktop uses a tighter inline layout. */
    variant: 'mobile' | 'desktop';
    paymentMethodType: PaymentMethodType;
    onPaymentMethodChange: (method: PaymentMethodType) => void;
    onAddCardOrAccountClick: () => Promise<void>;
    /** Whether Stripe is enabled (1 = enabled, 0 = disabled) */
    isStripeEnabled?: boolean;
    /** Whether admin is impersonating a user */
    isImpersonating?: boolean;
}

/**
 * "Pay via" (Bank Account | Card) radio selector + the adjacent Add Card / Add Account button.
 * Rendered once for the mobile header and once for the desktop header, with layout differing only
 * in wrapping Stack sx.
 */
export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
    variant,
    paymentMethodType,
    onPaymentMethodChange,
    onAddCardOrAccountClick,
    isStripeEnabled = false,
    isImpersonating = false,
}) => {
    const { t } = useTranslation();

    const pillContent = (
        <Stack direction={'row'} gap={1.5} sx={{ py: 1.8, px: 2, bgcolor: colors.primaryLight[5], borderRadius: 2 }}>
            <Typography
                variant="subtitle2"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center' }}
            >
                {t('payments.createPayment.payVia')}
            </Typography>
            <RadioGroup
                row
                value={paymentMethodType}
                onChange={(_, value) => onPaymentMethodChange(value as PaymentMethodType)}
                sx={{ gap: 0.5 }}
            >
                <FormControlLabel
                    sx={{ m: 0 }}
                    value="bank_account"
                    control={<CustomRadio size="small" />}
                    label={
                        <Typography variant="subtitle2" mb={0.1}>
                            {t('payments.createPayment.bankAccount')}
                        </Typography>
                    }
                />
                {isStripeEnabled && (
                    <FormControlLabel
                        sx={{ m: 0 }}
                        value="card"
                        control={<CustomRadio size="small" />}
                        label={
                            <Typography variant="subtitle2" mb={0.1}>
                                {t('payments.createPayment.card')}
                            </Typography>
                        }
                    />
                )}
            </RadioGroup>
        </Stack>
    );

    const addButton = (
        <ButtonComponent
            variant="contained"
            onClick={onAddCardOrAccountClick}
            disabled={false}
            startIcon={variant === 'mobile' ? <span className="icon-plus" /> : undefined}
            endIcon={variant === 'mobile' ? undefined : <span className="icon-plus" />}
            sx={addAccountButtonSx}
        >
            {paymentMethodType === 'card' ? t('makePayment.addCard') : t('BankAccounts.addAccount')}
        </ButtonComponent>
    );

    const renderMobileSegment = (value: PaymentMethodType, label: string) => (
        <FormControlLabel
            key={value}
            value={value}
            control={<CustomRadio size="small" />}
            label={
                <Typography variant="subtitle2" mb={0.1}>
                    {label}
                </Typography>
            }
            sx={mobilePaymentMethodSegmentSx(paymentMethodType === value)}
        />
    );

    if (variant === 'mobile') {
        return (
            <Stack gap={1.5} width={'100%'} mt={2}>
                <Stack direction={'row'} alignItems="center" justifyContent={'space-between'} width={'100%'}>
                    <Typography variant="subtitle2" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {t('payments.createPayment.payVia')}
                    </Typography>
                    {!isImpersonating && addButton}
                </Stack>
                <RadioGroup
                    row
                    value={paymentMethodType}
                    onChange={(_, value) => onPaymentMethodChange(value as PaymentMethodType)}
                    sx={mobilePaymentMethodTrackSx}
                >
                    {renderMobileSegment('bank_account', t('payments.createPayment.bankAccount'))}
                    {isStripeEnabled && renderMobileSegment('card', t('payments.createPayment.card'))}
                </RadioGroup>
            </Stack>
        );
    }

    return (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
            {pillContent}
            {!isImpersonating && addButton}
        </Stack>
    );
};
